import React, { useState, useEffect } from "react";

import Box from "@mui/material/Box";

import { Calendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "react-big-calendar/lib/css/react-big-calendar.css";

import moment from "moment";

import { getEvent } from "../query-functions";

import ListView from "../../../../events/components/event/CalendarView/ListView";
import EventWrapper, {
    getBackgroundFromEventStatus,
} from "../../../../events/components/event/CalendarView/EventWrapper";
import Toolbar from "../../../../events/components/event/CalendarView/Toolbar";

const localizer = momentLocalizer(moment);

const views = {
    month: true,
    week: true,
    work_week: true,
    day: true,
    agenda: ListView,
};

const TechniciansCalendar = ({
    clientID,
    calendarEvents,
    dateFilter,
    formatString,
    setDateFilter,
    setSelectedEvent,
    isLoading,
    isMobileScreenSize,
}) => {
    const [currentView, setCurrentView] = useState("agenda");

    const handleEventClick = (eventID) => {
        getEvent({
            clientID,
            eventID,
        }).then((event) => {
            let end;

            if (event.autoStopTime) {
                end = moment(event.autoStopTime).toDate();
            } else if (event.estRuntime) {
                end = moment(event.startDateTime)
                    .add(event.estRuntime, "minutes")
                    .toDate();
            } else if (event.stopDateTime) {
                end = moment(event.stopDateTime).toDate();
            } else {
                end = moment(event.startDateTime).toDate();
            }

            setSelectedEvent({
                rawEvent: { ...event },
                id: event.eventID,
                status: event.eventStatus,
                title: event.title,
                start: moment(event.startDateTime).toDate(),
                end,
                technicians: event.technicians,
            });
        });
    };

    const handleRangeChange = (range) => {
        if (range.start && range.end) {
            setDateFilter({
                startDate: moment(range.start)
                    .startOf("day")
                    .format(formatString),
                endDate: moment(range.end).endOf("day").format(formatString),
            });
        }

        if (range[0] && range[6]) {
            setDateFilter({
                startDate: moment(range[0]).format(formatString),
                endDate: moment(range[6]).format(formatString),
            });
        }

        if (range.length === 5) {
            setDateFilter({
                startDate: moment(range[0]).format(formatString),
                endDate: moment(range[4]).format(formatString),
            });
        }

        if (range.length === 1) {
            setDateFilter({
                startDate: moment(range[0]).startOf("day").format(formatString),
                endDate: moment(range[0]).endOf("day").format(formatString),
            });
        }

        setSelectedEvent(null);
    };

    const handleViewChange = (view) => {
        setCurrentView(view);
    };

    if (isMobileScreenSize) {
        return (
            <MobileCalendar
                events={calendarEvents}
                dateFilter={dateFilter}
                setDateFilter={setDateFilter}
                formatString={formatString}
                handleEventClick={handleEventClick}
            />
        );
    }

    return (
        <Calendar
            localizer={localizer}
            startAccessor="start"
            endAccessor="end"
            events={calendarEvents || []}
            style={{ height: "70vh", minHeight: "700px" }}
            dayLayoutAlgorithm="no-overlap"
            onSelectEvent={(event) => handleEventClick(event.id)}
            defaultView="agenda"
            onRangeChange={handleRangeChange}
            views={views}
            onView={handleViewChange}
            includeTechnicians
            isLoading={isLoading}
            components={{
                eventWrapper: (props) => {
                    return (
                        <EventWrapper
                            {...props}
                            getEvents={() => calendarEvents}
                        />
                    );
                },
                toolbar: (props) => {
                    return (
                        <Toolbar
                            loading={isLoading}
                            {...props}
                            currentView={currentView}
                        />
                    );
                },
            }}
        />
    );
};

export default TechniciansCalendar;

// creates a calendar by taking in the day and month and year and returns the calendar for that month
const MobileCalendar = ({
    events,
    dateFilter,
    setDateFilter,
    formatString,
    handleEventClick,
}) => {
    const month = moment(dateFilter.startDate).month();
    const year = moment(dateFilter.startDate).year();
    const day = moment(dateFilter.startDate).date();

    const startingDay = moment(`${year}-${month + 1}-01`).day();
    const monthLength = moment(`${year}-${month + 1}-01`).daysInMonth();

    const [selectedDate, setSelectedDate] = useState();

    useEffect(() => {
        setDateFilter({
            startDate: moment().startOf("month").format(formatString),
            endDate: moment().endOf("month").format(formatString),
        });

        setSelectedDate(moment(`${year}-${month + 1}-${day}`));
    }, []); // this effect must be ran to change the default date filter value, as the agenda view has a different range than the mobile month view

    const handleDateChange = ({ year, month, date, specificDate }) => {
        if (specificDate) {
            setSelectedDate(moment(`${year}-${month + 1}-${date}`));
            return;
        }

        setDateFilter({
            startDate: moment(`${year}-${month + 1}-${date}`)
                .startOf("month")
                .format(formatString),
            endDate: moment(`${year}-${month + 1}-${date}`)
                .endOf("month")
                .format(formatString),
        });
    };

    let calendar = [];

    let week = [];
    for (let i = 0; i < startingDay; i++) {
        week.push(null);
    }

    for (let i = 1; i <= monthLength; i++) {
        week.push(i);
        if (week.length === 7) {
            calendar.push(week);
            week = [];
        }
    }

    if (week.length > 0) {
        calendar.push(week);
    }

    // maps through the calendar and creates a div for each day
    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                }}
            >
                {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => {
                    return (
                        <Box
                            sx={{
                                width: "14%",
                                mb: 1,
                                textAlign: "center",
                            }}
                            key={index}
                        >
                            {day}
                        </Box>
                    );
                })}
            </Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    mb: 1,
                    p: 1,
                    backgroundColor: "primary.main",
                    color: "white",
                }}
            >
                <Box
                    onClick={() => {
                        if (month === 0) {
                            handleDateChange({
                                year: year - 1,
                                month: 11,
                                date: day,
                            });
                        } else {
                            handleDateChange({
                                year,
                                month: month - 1,
                                date: day,
                            });
                        }
                    }}
                    sx={{
                        width: "100%",
                        textAlign: "left",
                    }}
                >
                    <i className="fas fa-chevron-left"></i>
                </Box>

                <Box
                    sx={{
                        whiteSpace: "nowrap",
                    }}
                >
                    {moment().month(month).year(year).format("MMMM YYYY")}
                </Box>

                <Box
                    sx={{
                        width: "100%",
                        textAlign: "right",
                    }}
                    onClick={() => {
                        if (month === 11) {
                            handleDateChange({
                                year: year + 1,
                                month: 0,
                                date: day,
                            });
                        } else {
                            handleDateChange({
                                year,
                                month: month + 1,
                                date: day,
                            });
                        }
                    }}
                >
                    <i className="fas fa-chevron-right"></i>
                </Box>
            </Box>
            {calendar.map((week, index) => {
                return (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            width: "100%",
                        }}
                        key={index}
                    >
                        {week.map((day, index) => {
                            const isToday = moment().isSame(
                                moment(`${year}-${month + 1}-${day}`),
                                "day"
                            );

                            const isSelectDate = moment(selectedDate).isSame(
                                moment(`${year}-${month + 1}-${day}`),
                                "day"
                            );

                            // if the day is null, then it is an empty day
                            if (day === null) {
                                return (
                                    <Box
                                        sx={{
                                            width: "14%",
                                            mb: 1,
                                        }}
                                        key={index}
                                    ></Box>
                                );
                            }

                            return (
                                <Box
                                    sx={{
                                        width: "14%",
                                        mb: 1,
                                        textAlign: "center",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                    key={index}
                                    onClick={() => {
                                        handleDateChange({
                                            year,
                                            month,
                                            date: day,
                                            specificDate: true,
                                        });
                                    }}
                                >
                                    <Box
                                        sx={{
                                            borderRadius: "50%",
                                            py: 0.75,
                                            px: 1,
                                            backgroundColor:
                                                isToday && "primary.light",
                                            color: isToday && "white",

                                            ...(isSelectDate && {
                                                backgroundColor: "primary.main",
                                                color: "white",
                                            }),
                                        }}
                                    >
                                        {day < 10 ? `0${day}` : day}
                                    </Box>

                                    {/* displays a dot under the number for every event on that day */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 0.5,
                                            mt: 0.5,
                                            height: "4px",
                                        }}
                                    >
                                        {events &&
                                            events
                                                .filter((event) => {
                                                    const eventStart = moment(
                                                        event.start
                                                    );
                                                    const eventEnd = moment(
                                                        event.end
                                                    );

                                                    return (
                                                        eventStart.isSame(
                                                            moment(
                                                                `${year}-${
                                                                    month + 1
                                                                }-${day}`
                                                            ),
                                                            "day"
                                                        ) ||
                                                        eventEnd.isSame(
                                                            moment(
                                                                `${year}-${
                                                                    month + 1
                                                                }-${day}`
                                                            ),
                                                            "day"
                                                        )
                                                    );
                                                })
                                                .slice(0, 4)
                                                .map((event, index) => {
                                                    return (
                                                        <Box
                                                            sx={{
                                                                width: "4px",
                                                                height: "4px",
                                                                backgroundColor:
                                                                    getBackgroundFromEventStatus(
                                                                        event.status
                                                                    ),
                                                                borderRadius:
                                                                    "50%",
                                                            }}
                                                            key={index}
                                                        ></Box>
                                                    );
                                                })}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                );
            })}

            <MobileCalendarDayView
                events={events}
                date={moment(selectedDate || dateFilter.startDate).format(
                    "YYYY-MM-DD"
                )}
                handleEventClick={handleEventClick}
            />
        </>
    );
};

const MobileCalendarDayView = ({ events, date, handleEventClick }) => {
    return (
        <Calendar
            key={"MobileCalendarDayView"}
            localizer={localizer}
            startAccessor="start"
            endAccessor="end"
            onNavigate={() => {}}
            events={events || []}
            style={{ height: "70vh", minHeight: "700px" }}
            onSelectEvent={(event) => handleEventClick(event.id)}
            date={moment(date).toDate()}
            defaultDate={moment(date).toDate()}
            defaultView="day"
            components={{
                header: () => null,
                toolbar: (props) => {
                    return null;
                },
                eventWrapper: (props) => {
                    return <EventWrapper {...props} getEvents={() => events} />;
                },
            }}
        />
    );
};
